import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, User } from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '@/components/common/PageContainer'
import { AppButton } from '@/components/rfid/AppButton'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { BRAND } from '@/constants/brand'
import { useAuthStore } from '@/stores/authStore'
import { useRfidSessionStore } from '@/stores/rfidSessionStore'
import { getEmployeeCode } from '@/utils/employeeCode'
import { displayUserName, formatDateTime } from '@/utils/cn'

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-2.5 last:mb-0">
      <div className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="break-all font-mono text-[13px] font-semibold text-foreground">
        {value || '—'}
      </div>
    </div>
  )
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const [loggingOut, setLoggingOut] = useState(false)

  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerType = useRfidSessionStore((s) => s.scannerType)
  const deviceConnected = useRfidSessionStore((s) => s.deviceConnected)
  const lastScannerConnectAt = useRfidSessionStore((s) => s.lastScannerConnectAt)

  const employeeCode = getEmployeeCode(user)
  const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0'

  const onLogout = async () => {
    setLoggingOut(true)
    try {
      await logout()
      toast.message('Signed out')
      navigate('/login', { replace: true })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Logout failed')
    } finally {
      setLoggingOut(false)
    }
  }

  return (
    <PageContainer fluid className="!px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title="Profile"
          subtitle="Account and device session"
          onBack={() => navigate('/')}
        />

        <div className="rfid-wizard-body flex flex-1 flex-col gap-4 overflow-y-auto">
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-5 text-center">
            <span
              className="mb-1 flex h-14 w-14 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: BRAND }}
            >
              <User className="h-7 w-7" strokeWidth={2} />
            </span>
            <div className="text-xl font-black text-foreground">{displayUserName(user)}</div>
            {user?.email ? (
              <div className="text-[13px] font-semibold text-muted-foreground">{user.email}</div>
            ) : null}
            {employeeCode ? (
              <span
                className="mt-1 rounded-lg border px-2.5 py-1 text-[11px] font-extrabold"
                style={{ color: BRAND, borderColor: BRAND }}
              >
                {employeeCode}
              </span>
            ) : null}
          </div>

          <div className="rounded-[14px] border border-border bg-card p-3.5">
            <div className="mb-3 text-[13px] font-black tracking-wide text-foreground">Account</div>
            <InfoRow label="Name" value={user?.name ?? displayUserName(user)} />
            <InfoRow label="Email" value={user?.email ?? '—'} />
            <InfoRow label="Employee code" value={employeeCode || '—'} />
            <InfoRow label="App version" value={appVersion} />
          </div>

          <div className="rounded-[14px] border border-border bg-card p-3.5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="text-[13px] font-black tracking-wide text-foreground">Scanner</div>
              <span
                className="rounded-lg px-2 py-0.5 text-[9px] font-black"
                style={{
                  color: scannerConnected ? '#15803D' : BRAND,
                  backgroundColor: scannerConnected ? '#ECFDF5' : `${BRAND}18`,
                }}
              >
                {scannerConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <InfoRow label="Status" value={scannerConnected ? 'Connected' : 'Disconnected'} />
            <InfoRow label="Type" value={scannerType ?? '—'} />
            <InfoRow label="Device" value={deviceConnected?.rfid_device ?? '—'} />
            <InfoRow label="Last connect" value={formatDateTime(lastScannerConnectAt)} />
          </div>

          <AppButton
            block
            variant="outline"
            loading={loggingOut}
            onClick={() => void onLogout()}
            className="mt-2"
          >
            <LogOut className="h-[18px] w-[18px]" style={{ color: BRAND }} strokeWidth={2} />
            Log out
          </AppButton>
        </div>
      </div>
    </PageContainer>
  )
}
